import { inject, Injectable } from '@angular/core';
import {
  CollectionOperationRequest,
  CollectionRequestBuilder,
  YYYY_MM_DD_HH_MM_SS,
} from '@web/common';
import {
  ConfirmationService,
  DateFormatService,
  FileSaverService,
  InPostPointSelect,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { map, Observable, switchMap, tap } from 'rxjs';
import { AdminOrderApiService } from '../api/services/admin-order-api.service';
import { AdminOrderCollectionFilter } from '../models/admin-order-collection-filter';
import { AdminOrderCollectionItem } from '../models/admin-order-collection-item';
import { AdminOrderBuyer } from '../models/admin-order-buyer';
import {
  orderItemRefundSummaryFromResponse,
  orderRefundCollectionItemFromResponse,
  orderShipmentRefundSummaryFromResponse,
  refundSummaryFromResponse,
  shippingMethodDictionaryFromResponse,
} from '@web/shared/module/shop';
import {
  dictionaryCountryFromResponse,
  phoneNumberWithPrefixFromResponse,
  phoneNumberWithPrefixToRequest,
} from '@web/shared/module/dictionary';
import { executeOrNull, Guid, Nillable, once, valueOrDefault } from '@web/utils';
import { AdminOrderShipmentComponentGroupType } from '../enums/admin-order-shipment-component-group-type';
import { AdminOrderShipmentItemRequest } from '../api/requests/admin-order-shipment-item.request';
import { AdminOrderShipmentFormItem } from '../models/admin-order-shipment-form-item';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { AdminOrderDetails } from '../models/admin-order-details';
import { AdminOrderBillingAddress } from '../models/admin-order-billing-address';
import { AdminOrderShippingAddress } from '../models/admin-order-shipping-address';
import { AdminOrderItem } from '../models/admin-order-item';
import { AdminOrderItemAttribute } from '../models/admin-order-item-attribute';
import { AdminOrderPayment } from '../models/admin-order-payment';
import { AdminOrderShipment } from '../models/admin-order-shipment';
import { AdminPaymentMethod } from '../models/admin-payment-method';
import { AdminOrderShipmentTracking } from '../models/admin-order-shipment-tracking';
import { AdminOrderProduct } from '../models/admin-order-product';
import { AdminOrderInvoice } from '../models/admin-order-invoice';
import { AdminAdditionalShippingData } from '../models/admin-additional-shipping-data';
import { AdminCreateAdditionalShipmentRequest } from '../api/requests/admin-create-additional-shipment.request';
import { AdminOrderShippingAddressForm } from '../models/admin-order-shipping-address-form';
import { AdminOrderShippingAddressUpdateRequest } from '../api/requests/admin-order-shipping-address-update.request';
import { AdminOrderRefundData } from '../models/admin-order-refund-data';
import { AdminOrderRefundDataItem } from '../models/admin-order-refund-data-item';
import { AdminOrderRefundDataShipment } from '../models/admin-order-refund-data-shipment';
import { AdminOrderPartialRefundRequest } from '../api/requests/admin-order-partial-refund.request';
import { AdminOwner } from '../models/admin-owner';
import { AdminOrderItemComponent } from '../models/admin-order-item-component';
import { AdminOrderItemComponentAttribute } from '../models/admin-order-item-component-attribute';
import { AdminOrderItemComponentGroup } from '../models/admin-order-item-component-group';
import { AdminOrderShipmentComponentGroup } from '../models/admin-order-shipment-component-group';
import { AdminOrderItemComponentResponse } from '../api/responses/admin-order-item-component.response';

@Injectable({
  providedIn: 'root',
})
export class AdminOrderDataSourceService {
  private readonly orderApi = inject(AdminOrderApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);
  private readonly fileSaver = inject(FileSaverService);
  private readonly dateFormat = inject(DateFormatService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminOrderCollectionFilter>,
  ): Observable<TableDataPayload<AdminOrderCollectionItem>> {
    /* prettier-ignore */
    return this.orderApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('number').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('buyerDignity').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('shippingCountries').build())
                .appendText((builder) => builder.equalFor('buyerUserId').build())
                .appendText((builder) => builder.equalFor('number').build())
                .appendText((builder) => builder.equalFor('hasError').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('totalPrice').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('paymentMethod').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('products').build())
                .appendText((builder) => builder.inFor('status').build())
                .appendText((builder) => builder.inFor('shippingMethodIds').build())
                .appendText((builder) => builder.inFor('shippingStatuses').build())
                .appendText((builder) => builder.equalFor('variants').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminOrderCollectionItem({
                    canBeCancelled: item.contextOperations.canBeCancelled,
                    canBeMarkAsPaid: item.contextOperations.canBeMarkAsPaid,
                    canBeRefunded: item.contextOperations.canBeRefunded,
                    canBeViewed: item.contextOperations.canBeViewed,
                    createdAt: item.createdAt,
                    id: item.id,
                    hasError: valueOrDefault(item.hasError, false),
                    errorMessage: item.errorMessage,
                    comment: item.comment,
                    skinType: item.skinType,
                    variants: valueOrDefault(item.variants, []),
                    shippingMethods: item.shippingMethods.map(method => shippingMethodDictionaryFromResponse(method)),
                    paymentMethod: new AdminPaymentMethod({
                      name: item.paymentMethod.name,
                      code: item.paymentMethod.code,
                      id: item.paymentMethod.id,
                    }),
                    buyer: new AdminOrderBuyer({
                      dignity: item.buyer.dignity,
                      userId: item.buyer.userId,
                    }),
                    products: item.products.map((product) =>  new AdminOrderProduct({
                      name: product.name,
                      quantity:product.quantity,
                    })),
                    invoices: item.invoices.map((invoice) =>  new AdminOrderInvoice({
                      id: invoice.id,
                      number:invoice.number,
                      canBeDownloaded: invoice.contextOperations.canBeDownloaded,
                    })),
                    number: item.number,
                    status: item.status,
                    totalPrice: item.totalPrice,
                    refundSummary: executeOrNull(item.refundSummary, (summary) => refundSummaryFromResponse(summary)),
                    shippingCountries: item.shippingCountries.map((country) => dictionaryCountryFromResponse(country)),
                    shipments: item.shipments.map((tracking) => new AdminOrderShipmentTracking({
                      status: tracking.status,
                      id: tracking.id,
                      trackingUrl: tracking.trackingUrl,
                      scheduledShippingDate: tracking.scheduledShippingDate,
                    }))
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        )
      );
  }

  public createAdditionalShipmentByData(params: {
    orderId: Guid;
    data: AdminAdditionalShippingData;
  }): Observable<void> {
    const request = new AdminCreateAdditionalShipmentRequest({
      size: params.data.size,
      scheduledShippingDate: params.data.scheduledShippingDate,
      comment: params.data.comment,
      boxes: this.toShipmentItemRequests(params.data.boxes),
      products: this.toShipmentItemRequests(params.data.products),
      cosmetics: this.toShipmentItemRequests(params.data.cosmetics),
    });

    return this.orderApi.createAdditionalShipment({
      orderId: params.orderId,
      request,
    });
  }

  public fetchDetailsById(id: Guid): Observable<AdminOrderDetails> {
    return this.orderApi.fetchDetailsById({ id }).pipe(
      map(
        (response) =>
          new AdminOrderDetails({
            canBeCancelled: response.contextOperations.canBeCancelled,
            canBeMarkAsPaid: response.contextOperations.canBeMarkAsPaid,
            canBeCompleted: response.contextOperations.canBeCompleted,
            canBeRefunded: response.contextOperations.canBeRefunded,
            id: response.id,
            number: response.number,
            status: response.status,
            totalPrice: response.totalPrice,
            refundSummary: executeOrNull(response.refundSummary, (summary) =>
              refundSummaryFromResponse(summary),
            ),
            totalItems: response.totalItems,
            createdAt: response.createdAt,
            comment: response.comment,
            owner: executeOrNull(
              response.owner,
              (owner) =>
                new AdminOwner({
                  id: owner.id,
                  name: owner.name,
                  email: owner.email,
                }),
            ),
            billingAddress: new AdminOrderBillingAddress({
              city: response.billingAddress.city,
              dignity: response.billingAddress.dignity,
              email: response.billingAddress.email,
              firstName: response.billingAddress.firstName,
              lastName: response.billingAddress.lastName,
              phone: phoneNumberWithPrefixFromResponse(
                response.billingAddress.phone,
              ),
              postCode: response.billingAddress.postCode,
              streetLine1: response.billingAddress.streetLine1,
              country: dictionaryCountryFromResponse(
                response.billingAddress.country,
              ),
              streetLine2: response.billingAddress.streetLine2,
              state: response.billingAddress.state,
              companyName: response.billingAddress.companyName,
              companyNip: response.billingAddress.companyNip,
            }),
            items: response.items.map(
              (item) =>
                new AdminOrderItem({
                  name: item.name,
                  subName: item.subName,
                  slug: item.slug,
                  type: item.type,
                  quantity: item.quantity,
                  unitPrice: item.unitPrice,
                  price: item.price,
                  refundSummary: executeOrNull(item.refundSummary, (summary) =>
                    orderItemRefundSummaryFromResponse(summary),
                  ),
                  featuredFile: executeOrNull(item.featuredFile, (file) =>
                    multimediaFromResponse(file),
                  ),
                  attributes: valueOrDefault(item.attributes, []).map(
                    (attr) =>
                      new AdminOrderItemAttribute({
                        id: attr.id,
                        value: attr.value,
                        productAttributeId: attr.productAttribute.id,
                        productAttributeLabel: attr.productAttribute.label,
                      }),
                  ),
                  componentGroups: valueOrDefault(item.componentGroups, []).map(
                    (group) =>
                      new AdminOrderItemComponentGroup({
                        name: group.name,
                        isRandomSelected: group.isRandomSelected,
                        components: valueOrDefault(group.components, []).map(
                          (component) => this.mapOrderComponent(component),
                        ),
                      }),
                  ),
                }),
            ),
            payments: response.payments.map(
              (payment) =>
                new AdminOrderPayment({
                  number: payment.number,
                  status: payment.status,
                  createdAt: payment.createdAt,
                  paymentMethodCode: payment.method.code,
                  paymentMethodId: payment.method.id,
                  paymentMethodName: payment.method.name,
                }),
            ),
            shipments: valueOrDefault(response.shipments, []).map(
              (shipment) =>
                new AdminOrderShipment({
                  canBeDeleted: shipment.contextOperations.canBeDeleted,
                  canBeSent: shipment.contextOperations.canBeSent,
                  canBeEdited: shipment.contextOperations.canBeEdited,
                  id: shipment.id,
                  status: shipment.status,
                  price: shipment.price,
                  createdAt: shipment.createdAt,
                  methodId: shipment.method.id,
                  methodName: shipment.method.name,
                  packageSize: shipment.packageSize,
                  comment: shipment.comment,
                  methodCode: shipment.method.code,
                  scheduledShippingDate: shipment.scheduledShippingDate,
                  componentGroups: valueOrDefault(
                    shipment.componentGroups,
                    [],
                  ).map(
                    (group) =>
                      new AdminOrderShipmentComponentGroup({
                        name: group.name,
                        type: group.type,
                        components: valueOrDefault(group.components, []).map(
                          (component) => this.mapOrderComponent(component),
                        ),
                      }),
                  ),
                  address: new AdminOrderShippingAddress({
                    city: shipment.address.city,
                    dignity: shipment.address.dignity,
                    email: shipment.address.email,
                    firstName: shipment.address.firstName,
                    lastName: shipment.address.lastName,
                    phone: phoneNumberWithPrefixFromResponse(
                      shipment.address.phone,
                    ),
                    postCode: shipment.address.postCode,
                    streetLine1: shipment.address.streetLine1,
                    country: dictionaryCountryFromResponse(
                      shipment.address.country,
                    ),
                    streetLine2: shipment.address.streetLine2,
                    state: shipment.address.state,
                    companyName: shipment.address.companyName,
                    shippingPoint: shipment.address.shippingPoint,
                    shippingPointName: shipment.address.shippingPointName,
                  }),
                  refundSummary: executeOrNull(
                    shipment.refundSummary,
                    (summary) =>
                      orderShipmentRefundSummaryFromResponse(summary),
                  ),
                  trackingUrl: shipment.trackingUrl,
                }),
            ),
            invoice: executeOrNull(
              response.invoice,
              (invoice) =>
                new AdminOrderInvoice({
                  id: invoice.id,
                  number: invoice.number,
                  canBeDownloaded: invoice.contextOperations.canBeDownloaded,
                }),
            ),
            temporaryInvoice: executeOrNull(
              response.temporaryInvoice,
              (invoice) =>
                new AdminOrderInvoice({
                  id: invoice.id,
                  number: invoice.number,
                  canBeDownloaded: invoice.contextOperations.canBeDownloaded,
                }),
            ),
            paidAt: response.paidAt,
            refundedAt: response.refundedAt,
            completedAt: response.completedAt,
            cancelledAt: response.cancelledAt,
            refunds: valueOrDefault(response.refunds, []).map((refund) =>
              orderRefundCollectionItemFromResponse(refund),
            ),
          }),
      ),
    );
  }

  public confirmAndFullRefundOrderById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wykonać pełny zwrot zamówienia?')
          .accept(() => {
            this.orderApi
              .fullRefundOrderById(id)
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Zlecenie zwrotu zostało wykonane pomyślnie.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public fetchRefundDataById(id: Guid): Observable<AdminOrderRefundData> {
    return this.orderApi.fetchRefundDataById({ id }).pipe(
      map(
        (response) =>
          new AdminOrderRefundData({
            orderId: response.orderId,
            orderNumber: response.orderNumber,
            items: response.items.map(
              (item) =>
                new AdminOrderRefundDataItem({
                  id: item.id,
                  name: item.name,
                  subName: item.subName,
                  type: item.type,
                  quantity: item.quantity,
                  unitPrice: item.unitPrice,
                  price: item.price,
                  slug: item.slug,
                  featuredFile: executeOrNull(item.featuredFile, (file) =>
                    multimediaFromResponse(file),
                  ),
                  refundSummary: executeOrNull(item.refundSummary, (summary) =>
                    orderItemRefundSummaryFromResponse(summary),
                  ),
                }),
            ),
            shipments: response.shipments.map(
              (shipment) =>
                new AdminOrderRefundDataShipment({
                  id: shipment.id,
                  price: shipment.price,
                  methodName: shipment.method.name,
                  methodCode: shipment.method.code,
                  refundSummary: executeOrNull(
                    shipment.refundSummary,
                    (summary) =>
                      orderShipmentRefundSummaryFromResponse(summary),
                  ),
                }),
            ),
          }),
      ),
    );
  }

  public confirmAndPartialRefundOrder(params: {
    orderId: Guid;
    items: Array<{ id: Guid; refundPrice: string }>;
    shipments: Array<{ id: Guid; refundPrice: string }>;
    comment?: Nillable<string>;
  }): Observable<void> {
    const request = new AdminOrderPartialRefundRequest({
      items: params.items,
      shipments: params.shipments,
      comment: params.comment,
    });

    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wykonać częściowy zwrot zamówienia?')
          .accept(() => {
            this.orderApi
              .partialRefundOrderById(params.orderId, request)
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((toastBuilder) =>
                    toastBuilder
                      .initSuccess(
                        'Częściowy zwrot zamówienia został wykonany pomyślnie.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndCancelById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz anulować wybrane zamówienie?')
          .accept(() => {
            this.orderApi
              .cancelById(id)
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Anulowanie zamówienia zostało wykonane pomyślnie.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndMarkAsPaidById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz oznaczyć wybrane zamówienie jako opłacone?',
          )
          .accept(() => {
            this.orderApi
              .markAsPaidById(id)
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Zamówienia zostało oznaczone jako opłacone.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public downloadLabelFor(params: {
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<void> {
    const labelFileName = `etykieta_${params.shipmentId}_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;

    return this.orderApi
      .downloadLabelFor({
        orderId: params.orderId,
        shipmentId: params.shipmentId,
      })
      .pipe(switchMap((blob) => this.fileSaver.saveAs(blob, labelFileName)));
  }

  public downloadLabels(params: { includedIds: Guid[] }): Observable<void> {
    const { includedIds } = params;
    const request = new CollectionOperationRequest({ includedIds });
    const labelsFileName = `etykiety_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;

    return this.orderApi
      .downloadLabels(request)
      .pipe(switchMap((blob) => this.fileSaver.saveAs(blob, labelsFileName)));
  }

  public markAsCompleteMultiple(params: {
    includedIds: Guid[];
  }): Observable<Guid> {
    const { includedIds } = params;
    const request = new CollectionOperationRequest({ includedIds });

    return this.orderApi
      .markAsCompleteMultiple(request)
      .pipe(map((response) => response.id));
  }

  public confirmAndMarkAsCompleteById(id: Guid): Observable<void> {
    const labelFileName = `etykieta_${this.dateFormat.format(new Date(), YYYY_MM_DD_HH_MM_SS)}`;

    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz oznaczyć wybrane zamówienie jako zatwierdzone?',
          )
          .accept(() => {
            this.orderApi
              .markAsCompleteById(id)
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrane zamówienie zostało oznaczone jako zatwiedzone. Za chwilę nastąpi pobranie etykiety.',
                      )
                      .build(),
                  ),
                ),
                switchMap((blob) => this.fileSaver.saveAs(blob, labelFileName)),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndRemoveShipmentFromOrder(params: {
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<void> {
    const { orderId, shipmentId } = params;

    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz usunąć wybraną przesyłkę z zamówienia?')
          .accept(() => {
            this.orderApi
              .removeShipmentFromOrder({ shipmentId, orderId })
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Wybrana przesyłka została usunięta z zamówienia.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndGenerateAndSend(params: {
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<void> {
    const { orderId, shipmentId } = params;

    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz wygenerować etykietę oraz oznaczyć wybraną przesyłkę jako wysłana?',
          )
          .accept(() => {
            this.orderApi
              .generateAndSend({ orderId, shipmentId })
              .pipe(
                once(),
                tap(() =>
                  this.toast.show((builder) =>
                    builder
                      .initSuccess(
                        'Etykieta została wygenerowana, a przesyłka została oznaczona jako wysłana.',
                      )
                      .build(),
                  ),
                ),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public updateShippingAddressByFormData(param: {
    orderId: Guid;
    shipmentId: Guid;
    data: AdminOrderShippingAddressForm;
  }): Observable<void> {
    const { orderId, data, shipmentId } = param;
    const request = new AdminOrderShippingAddressUpdateRequest({
      city: data.city!,
      companyName: data.companyName,
      countryId: data.countryId!,
      email: data.email!,
      firstName: data.firstName!,
      lastName: data.lastName!,
      phone: phoneNumberWithPrefixToRequest(data.phone!),
      postCode: data.postCode!,
      state: data.state,
      streetLine1: data.streetLine1!,
      streetLine2: data.streetLine2,
      shippingMethod: data.shippingMethodId,
      shippingPoint: executeOrNull(data.shippingPoint, (point) => point.name),
      scheduledShippingDate: data.scheduledShippingDate!,
      comment: data.comment,
      packageSize: data.size!,
      boxes: this.toShipmentItemRequests(data.boxes),
      products: this.toShipmentItemRequests(data.products),
      cosmetics: this.toShipmentItemRequests(data.cosmetics),
    });

    return this.orderApi
      .updateShippingAddress({ orderId, shipmentId, request })
      .pipe(
        tap(() => {
          this.toast.show((builder) =>
            builder
              .initSuccess(
                'Wybrana przesyłka dla zamówienia została zaktualizowana.',
              )
              .build(),
          );
        }),
      );
  }

  public fetchShippingAddressFormById(params: {
    orderId: Guid;
    shipmentId: Guid;
  }): Observable<AdminOrderShippingAddressForm> {
    const { orderId, shipmentId } = params;
    return this.orderApi
      .fetchShippingAddressDetails({ orderId, shipmentId })
      .pipe(
        map((response) => {
          const componentGroups = valueOrDefault(
            response.componentGroups,
            [],
          ).map(
            (group) =>
              new AdminOrderShipmentComponentGroup({
                name: group.name,
                type: group.type,
                components: valueOrDefault(group.components, []).map(
                  (component) => this.mapOrderComponent(component),
                ),
              }),
          );
          const cosmetics = this.shipmentItemsByType(
            componentGroups,
            AdminOrderShipmentComponentGroupType.Cosmetic,
          );
          const boxes = this.shipmentItemsByType(
            componentGroups,
            AdminOrderShipmentComponentGroupType.Box,
          );
          const products = this.shipmentItemsByType(
            componentGroups,
            AdminOrderShipmentComponentGroupType.Product,
          );

          return new AdminOrderShippingAddressForm({
            city: response.address.city,
            companyName: response.address.companyName,
            countryId: response.address.country?.id,
            email: response.address.email,
            firstName: response.address.firstName,
            lastName: response.address.lastName,
            phone: phoneNumberWithPrefixFromResponse(response.address.phone),
            postCode: response.address.postCode,
            state: response.address.state,
            streetLine1: response.address.streetLine1,
            streetLine2: response.address.streetLine2,
            shippingMethodId: response.method.id,
            shippingPoint: executeOrNull(
              response.address.shippingPoint,
              (result) => {
                return new InPostPointSelect({
                  description: result,
                });
              },
            ),
            scheduledShippingDate: response.scheduledShippingDate,
            comment: response.comment,
            size: response.packageSize,
            boxes,
            products,
            cosmetics,
            boxesReadonlyReason: this.readonlyReasonForShipmentItems(boxes),
            productsReadonlyReason:
              this.readonlyReasonForShipmentItems(products),
            cosmeticsReadonlyReason:
              this.readonlyReasonForShipmentItems(cosmetics),
          });
        }),
      );
  }

  private toShipmentItemRequests(
    items: AdminOrderShipmentFormItem[],
  ): AdminOrderShipmentItemRequest[] | undefined {
    if (items.some((item) => !item.isResolved)) {
      return undefined;
    }

    return items.map(
      (item) =>
        new AdminOrderShipmentItemRequest({
          id: item.id!,
          quantity: item.quantity,
        }),
    );
  }

  private shipmentItemsByType(
    groups: AdminOrderShipmentComponentGroup[],
    type: AdminOrderShipmentComponentGroupType,
  ): AdminOrderShipmentFormItem[] {
    const group = groups.find((item) => item.type === type);

    return valueOrDefault(group?.components, []).map(
      (component) =>
        new AdminOrderShipmentFormItem({
          name: component.name,
          quantity: component.quantity,
          featuredFileAlt: component.featuredFile?.alt,
          featuredFileUrl: component.featuredFile?.url,
          sourceName: component.sourceName,
        }),
    );
  }

  private readonlyReasonForShipmentItems(
    items: AdminOrderShipmentFormItem[],
  ): Nillable<string> {
    if (items.length === 0 || items.every((item) => item.isResolved)) {
      return null;
    }

    return 'Te pozycje zostały już zapisane w tej przesyłce i obecnie nie można ich edytować w tym formularzu. Zapis pozostałych zmian nie usunie tych elementów z przesyłki.';
  }

  private mapOrderComponent(
    component: AdminOrderItemComponentResponse,
  ): AdminOrderItemComponent {
    return new AdminOrderItemComponent({
      name: component.name,
      quantity: component.quantity,
      featuredFile: executeOrNull(component.featuredFile, (file) =>
        multimediaFromResponse(file),
      ),
      sourceId: component.source?.id,
      sourceName: component.source?.name,
      attributes: valueOrDefault(component.attributes, []).map(
        (attribute) =>
          new AdminOrderItemComponentAttribute({
            id: attribute.id,
            label: attribute.label,
            value: attribute.value,
          }),
      ),
    });
  }
}
